import styled from 'styled-components';

export const Container = styled.section`
	padding-top: 15%;
	display: flex;
	justify-content: space-between;
	gap: 8rem;
	background: rgba(0, 0, 0, 0);
	position: relative;
	overflow: hidden;
	
	/* Animated background gradient */
	&::before {
		content: '';
		position: absolute;
		top: -50%;
		left: -50%;
		width: 200%;
		height: 200%;
		background: radial-gradient(circle at 50% 50%, 
			rgba(99, 102, 241, 0.08) 0%, 
			transparent 50%,
			rgba(139, 92, 246, 0.05) 100%);
		animation: rotateBackground 20s linear infinite;
		z-index: -1;
		pointer-events: none;
	}
	
	/* Floating particles */
	&::after {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		background-image: 
			radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.1) 1px, transparent 1px),
			radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.08) 1px, transparent 1px),
			radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.06) 1px, transparent 1px);
		background-size: 100px 100px, 120px 120px, 80px 80px;
		animation: floatParticles 30s linear infinite;
		z-index: -1;
		pointer-events: none;
	}
	
	@keyframes rotateBackground {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}
	
	@keyframes floatParticles {
		0% { transform: translateY(0); }
		100% { transform: translateY(-100px); }
	}
	
	.hero-text {
		position: relative;
		z-index: 1;
		
		& > p {
			font-size: clamp(1.8rem, 2.5vw, 2.2rem);
			color: var(--text-light);
			font-weight: 500;
		}

		.wave-emoji {
			display: inline-block;
			font-size: 1.2em;
			animation: wave 1.8s ease-in-out infinite;
			transform-origin: 70% 70%;
		}
		
		h1 {
			font-size: clamp(5rem, 8vw, 8rem);
			font-weight: 900;
			margin: 2rem 0;
			background: linear-gradient(135deg, 
				#6366f1 0%, 
				#8b5cf6 25%,
				#06b6d4 50%,
				#8b5cf6 75%,
				#6366f1 100%);
			background-size: 200% auto;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			background-clip: text;
			animation: shimmer 3s linear infinite;
			text-shadow: 0 0 40px rgba(99, 102, 241, 0.3);
			letter-spacing: -0.02em;
			line-height: 1.1;
			position: relative;
			
			&::after {
				content: attr(data-text);
				position: absolute;
				left: 0;
				top: 0;
				z-index: -1;
				background: linear-gradient(135deg, #6366f1, #8b5cf6);
				-webkit-background-clip: text;
				-webkit-text-fill-color: transparent;
				background-clip: text;
				filter: blur(20px);
				opacity: 0.5;
			}
		}

		h3 {
			font-size: clamp(2.2rem, 3vw, 2.8rem);
			background: linear-gradient(90deg, 
				#06b6d4 0%,
				#3b82f6 50%,
				#8b5cf6 100%);
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			background-clip: text;
			margin: 1.5rem 0;
			font-weight: 700;
			letter-spacing: -0.01em;
			position: relative;
			
			&::before {
				content: '';
				position: absolute;
				bottom: -0.5rem;
				left: 0;
				width: 0;
				height: 3px;
				background: linear-gradient(90deg, #06b6d4, #8b5cf6);
				border-radius: 2px;
				animation: expandLine 2s ease-out forwards;
			}
		}

		p.small-resume {
			margin-top: 2rem;
			margin-bottom: 3rem;
			line-height: 1.9;
			font-size: clamp(1.6rem, 2vw, 1.9rem);
			color: rgba(255, 255, 255, 0.85);
			font-weight: 400;
			max-width: 600px;
		}
	}
	
	@keyframes wave {
		0%, 100% { transform: rotate(0deg); }
		10%, 30% { transform: rotate(14deg); }
		20% { transform: rotate(-8deg); }
		40%, 100% { transform: rotate(0deg); }
	}
	
	@keyframes shimmer {
		0% { background-position: 0% center; }
		100% { background-position: 200% center; }
	}
	
	@keyframes expandLine {
		0% { width: 0; }
		100% { width: 120px; }
	}
	
	.achievement-badges {
		display: flex;
		flex-wrap: wrap;
		gap: 1.2rem;
		margin: 2.5rem 0;
		
		.badge {
			display: inline-flex;
			align-items: center;
			gap: 0.8rem;
			padding: 1rem 2rem;
			background: linear-gradient(135deg, 
				rgba(99, 102, 241, 0.15) 0%,
				rgba(139, 92, 246, 0.12) 100%);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			border: 2px solid transparent;
			background-clip: padding-box;
			border-radius: 2.5rem;
			font-size: 1.5rem;
			font-weight: 600;
			color: #ffffff;
			text-decoration: none;
			cursor: pointer;
			position: relative;
			overflow: hidden;
			transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
			box-shadow: 0 4px 15px rgba(99, 102, 241, 0.2),
						inset 0 1px 0 rgba(255, 255, 255, 0.1);
			
			/* Gradient border effect */
			&::before {
				content: '';
				position: absolute;
				inset: 0;
				border-radius: 2.5rem;
				padding: 2px;
				background: linear-gradient(135deg, #6366f1, #8b5cf6, #06b6d4);
				-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
				-webkit-mask-composite: xor;
				mask-composite: exclude;
				opacity: 0;
				transition: opacity 0.4s ease;
			}
			
			/* Shine effect */
			&::after {
				content: '';
				position: absolute;
				top: -50%;
				left: -50%;
				width: 200%;
				height: 200%;
				background: linear-gradient(
					45deg,
					transparent 30%,
					rgba(255, 255, 255, 0.15) 50%,
					transparent 70%
				);
				transform: translateX(-100%) translateY(-100%) rotate(45deg);
				transition: transform 0.6s ease;
			}
			
			&:hover {
				transform: translateY(-5px) scale(1.08);
				background: linear-gradient(135deg, 
					rgba(99, 102, 241, 0.25) 0%,
					rgba(139, 92, 246, 0.22) 100%);
				box-shadow: 0 10px 30px rgba(99, 102, 241, 0.4),
							0 0 0 1px rgba(99, 102, 241, 0.5),
							inset 0 1px 0 rgba(255, 255, 255, 0.2);
				
				&::before {
					opacity: 1;
				}
				
				&::after {
					transform: translateX(100%) translateY(100%) rotate(45deg);
				}
			}
			
			&:active {
				transform: translateY(-2px) scale(1.05);
			}
			
			.badge-icon {
				font-size: 2rem;
				filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
				animation: float 3s ease-in-out infinite;
				animation-delay: calc(var(--badge-index) * 0.2s);
			}
			
			span:not(.badge-icon) {
				position: relative;
				z-index: 1;
			}
		}
		
		.badge:nth-child(1) { --badge-index: 0; }
		.badge:nth-child(2) { --badge-index: 1; }
		.badge:nth-child(3) { --badge-index: 2; }
		.badge:nth-child(4) { --badge-index: 3; }
		.badge:nth-child(5) { --badge-index: 4; }
	}
	
	@keyframes float {
		0%, 100% { transform: translateY(0); }
		50% { transform: translateY(-5px); }
	}
	.social-media {
		display: inline-flex;
		align-items: center;
		justify-content: flex-start;
		gap: 1.5rem;
		padding-top: 3rem;
		padding-left: 0;

		.hability {
			transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
			border: 2px solid transparent;
			position: relative;
			overflow: hidden;
			
			/* Glassmorphism effect */
			background: linear-gradient(135deg, 
				rgba(99, 102, 241, 0.1) 0%,
				rgba(139, 92, 246, 0.08) 100%) !important;
			backdrop-filter: blur(8px);
			-webkit-backdrop-filter: blur(8px);
			
			/* Gradient border on hover */
			&::before {
				content: '';
				position: absolute;
				inset: 0;
				border-radius: 12px;
				padding: 2px;
				background: linear-gradient(135deg, #6366f1, #8b5cf6, #06b6d4);
				-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
				-webkit-mask-composite: xor;
				mask-composite: exclude;
				opacity: 0;
				transition: opacity 0.4s ease;
			}
			
			/* Shine sweep */
			&::after {
				content: '';
				position: absolute;
				top: 0;
				left: -100%;
				width: 100%;
				height: 100%;
				background: linear-gradient(90deg, 
					transparent, 
					rgba(255, 255, 255, 0.2), 
					transparent);
				transition: left 0.5s ease;
			}
			
			&:hover {
				transform: translateY(-8px) scale(1.1) rotateY(10deg);
				border-color: transparent;
				box-shadow: 0 15px 35px rgba(99, 102, 241, 0.4),
							0 0 20px rgba(139, 92, 246, 0.3);
				background: linear-gradient(135deg, 
					rgba(99, 102, 241, 0.2) 0%,
					rgba(139, 92, 246, 0.15) 100%) !important;
				
				&::before {
					opacity: 1;
				}
				
				&::after {
					left: 100%;
				}
				
				img {
					transform: scale(1.1) rotateZ(5deg);
					filter: brightness(1.2) drop-shadow(0 0 10px rgba(99, 102, 241, 0.5));
				}
			}
			
			img {
				transition: all 0.4s ease;
				filter: brightness(1);
			}
		}

		img,
		span {
			font-size: 3rem;
			width: 4rem;
		}
	}

	.button {
		margin-top: 3rem;
		padding: 1.8rem 4.5rem;
		font-size: 1.7rem;
		font-weight: 700;
		display: inline-block;
		background: linear-gradient(135deg, #6366f1, #8b5cf6);
		border: none;
		border-radius: 5rem;
		color: white;
		text-decoration: none;
		position: relative;
		overflow: hidden;
		transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
		box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
		
		/* Shine effect */
		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: -100%;
			width: 100%;
			height: 100%;
			background: linear-gradient(90deg, 
				transparent, 
				rgba(255, 255, 255, 0.3), 
				transparent);
			transition: left 0.6s ease;
		}
		
		&:hover {
			transform: translateY(-3px) scale(1.05);
			box-shadow: 0 15px 40px rgba(99, 102, 241, 0.5),
						0 0 20px rgba(139, 92, 246, 0.4);
			
			&::before {
				left: 100%;
			}
		}
		
		&:active {
			transform: translateY(-1px) scale(1.02);
		}
	}

	.hero-image {
		position: relative;
		perspective: 1000px;
		
		img {
			max-width: 300px;
			border-radius: 20px;
			position: relative;
			transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
			box-shadow: 0 20px 60px rgba(99, 102, 241, 0.3);
			border: 3px solid rgba(99, 102, 241, 0.2);
			
			/* Animated gradient border */
			&::before {
				content: '';
				position: absolute;
				inset: -3px;
				border-radius: 20px;
				background: linear-gradient(45deg, 
					#6366f1, #8b5cf6, #06b6d4, #8b5cf6, #6366f1);
				background-size: 300% 300%;
				z-index: -1;
				animation: gradientRotate 4s ease infinite;
				opacity: 0;
				transition: opacity 0.5s ease;
			}
			
			&:hover {
				transform: translateY(-10px) rotateY(5deg) scale(1.05);
				box-shadow: 0 30px 80px rgba(99, 102, 241, 0.5),
							0 0 40px rgba(139, 92, 246, 0.4);
				border-color: rgba(99, 102, 241, 0.5);
			}
		}
	}
	
	@keyframes gradientRotate {
		0%, 100% { background-position: 0% 50%; }
		50% { background-position: 100% 50%; }
	}

	@media (max-width: 300px) {
		display: block;
		margin-top: 150%;
		.hero-text {
			h1 {
				font-size: 5rem;
			}
		}

		.hero-image {
			display: none;
		}
	}

	@media (max-width: 400px) {
		margin-top: 35%;
	}
	@media (max-width: 480px) {
		margin-top: 45%;
	}
	@media (max-width: 768px) {
		flex-direction: column;
	}
`;
